// The review login (dev only, for Figma's App Review) — the client half of
// `apps/server/src/http/review-login.ts`.
//
// Raw `fetch`, not `httpPost`: a wrong password answers 401, and `httpPost`
// would read that as "your Loom sign-in is gone" and trip the app's one
// unauthorized reaction. Here a 401 only means "try again".

import { z } from "zod";

const StatusSchema = z.object({ available: z.boolean() });

/**
 * Is the review login open right now? Off on the server, the route does not
 * exist (404) — and any failure at all reads as "no", so the sign-in page
 * then looks exactly as it does without the feature.
 */
export const fetchReviewAvailable = async (): Promise<boolean> => {
  try {
    const res = await fetch("/auth/review/status", {
      method: "GET",
      credentials: "same-origin",
      headers: { accept: "application/json" },
    });
    if (res.status !== 200) return false;
    const parsed = StatusSchema.safeParse(await res.json());
    return parsed.success && parsed.data.available;
  } catch {
    return false;
  }
};

export type ReviewLoginResult = "ok" | "wrong" | "too_many" | "closed" | "failed";

export const submitReviewLogin = async (
  username: string,
  password: string,
): Promise<ReviewLoginResult> => {
  try {
    const res = await fetch("/auth/review/login", {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify({ username, password }),
    });
    switch (res.status) {
      case 200:
        return "ok";
      case 401:
        return "wrong";
      case 429:
        return "too_many";
      case 403:
        return "closed";
      default:
        return "failed";
    }
  } catch {
    return "failed";
  }
};
